import { AnnotationShape } from '@/web-components/import-wizard-v2/typings/common';
import { PrintArea } from '@/web-components/import-wizard-v2/typings/common';
import { lineWidthV2 } from '@/constants/areaLineWidth';
import {
  ArtworkPayload,
  LogoShape,
} from '@/web-components/import-wizard/typings/pintura';
import { px2mm, recalculateDPI } from '@/utils/common';
import { BACKGROUND_IMAGE_W } from '@/web-components/import-wizard-v2/constants';
import { ProductPrintArea } from '@midocean/webapps-shared-domain';

const CORRECTION_FACTOR = 0;

export function createShapeFromPayload(
  payload: ArtworkPayload,
  printableArea: PrintArea,
  printableAreaRotation: number,
  productPrintPosition: any,
  polygonDetails: any
): AnnotationShape {
  const centerPositionShape = getCenterShapePosition(
    payload.pngWidthPixels,
    payload.pngHeightPixels,
    productPrintPosition,
    printableArea,
    polygonDetails
  );

  const recalculatedHDPI = recalculateDPI(
    px2mm(
      centerPositionShape.width,
      productPrintPosition?.originalImageWebDpi as number
    ),
    payload.originalHorizontalResolutionDPIs,
    payload.originalWidthMM
  );

  const recalculatedVDPI = recalculateDPI(
    px2mm(
      centerPositionShape.height,
      productPrintPosition?.originalImageWebDpi as number
    ),
    payload.originalVerticalResolutionDPIs,
    payload.originalHeightMM
  );

  const shapeToAdd: AnnotationShape = {
    zindex: 0,
    kind: 'Logo',
    isRoundLogo: false,
    id: payload.personalizationArtworkId,
    title: payload.title,
    backgroundImage: URL.createObjectURL(payload.pngFile as Blob),
    pngFileWithoutConverters: payload.pngFileWithoutConverters,
    x: centerPositionShape.x,
    y: centerPositionShape.y,
    isInArea: true,
    width: centerPositionShape.width,
    height: centerPositionShape.height,
    aspectRatio: centerPositionShape.width / centerPositionShape.height,
    processed: '',
    isSelected: true,
    rotation: printableAreaRotation,
    pngHorizontalResolutionDPIs: Math.round(recalculatedHDPI),
    pngVerticalResolutionDPIs: Math.round(recalculatedVDPI),
    smallDetailsResult: payload.smallDetailsResult,
    customerId: payload.customerId,
    fileName: payload.fileName,
    thumbnailUrl: payload.thumbnailUrl,
    originalFileUrl: payload.originalFileUrl,
    pngFileUrl: payload.pngFileUrl,
    pngWidthPixels: payload.pngWidthPixels,
    pngHeightPixels: payload.pngHeightPixels,
    originalWidthMM: payload.originalWidthMM,
    originalHeightMM: payload.originalHeightMM,
    isVectorFileTrueVectorFile: payload.isVectorFileTrueVectorFile,
    hasGradient: payload.hasGradient,
    hasTransparency: payload.hasTransparency,
    isFileConvertedToCmykPdf: payload.isFileConvertedToCmykPdf,
    areCmykValuesCorrect: payload.areCmykValuesCorrect,
    spotColorsInOriginalFile: payload.spotColorsInOriginalFile,
    pmsColorsInPngFile: payload.pmsColorsInPngFile.map((color) => ({
      ...color,
      originalName: color.name,
    })),
    customerArtworkId: payload.customerArtworkId,
    pngFile: payload.pngFile,
    personalizationArtworkId: payload.personalizationArtworkId,
    originalHorizontalResolutionDPIs: payload.originalHorizontalResolutionDPIs,
    originalVerticalResolutionDPIs: payload.originalVerticalResolutionDPIs,
    variantArtworkImages: payload.variantArtworkImages,
    xOnProductMM: 0,
    yOnProductMM: 0,
    widthOnProductMM: 0,
    heightOnProductMM: 0,
    cmykPdfFileUrl: payload.cmykPdfFileUrl ? payload.cmykPdfFileUrl : '',
    traditionalTechniquePngFile: payload.traditionalTechniquePngFile,
    fullColorTechniquePngFile: payload.pngFile as Blob, // check this again,
    isOnRecommendedPrintPosition: false,
  };

  return shapeToAdd;
}

export function getCenterShapePosition(
  pngWidthPixels: number,
  pngHeightPixels: number,
  productPrintPosition: any,
  printableArea: PrintArea,
  polygonDetails: any
): {
  x: number;
  y: number;
  width: number;
  height: number;
} {
  const artworkSpaceConsumed = 97; // value in percents
  let calcArtworkWidth = 0;
  let calcArtworkHeight = 0;
  let shapeX = 0;
  let shapeY = 0;

  const rectLineWidth = (lineWidthV2 * BACKGROUND_IMAGE_W) / 100;

  const aspectRatioWH = pngWidthPixels / pngHeightPixels;
  const aspectRatioHW = pngHeightPixels / pngWidthPixels;

  if (printableArea?.kind === 'Rectangle') {
    const areaRect = {
      x: printableArea.x + rectLineWidth / 2,
      y: printableArea.y + rectLineWidth / 2,
      w: printableArea.width - rectLineWidth,
      h: printableArea.height - rectLineWidth,
    };

    const artworkRatio = pngWidthPixels / pngHeightPixels;
    const areaRatio = areaRect.w / areaRect.h;

    // Artwork is more landscape than area - fit to width
    if (artworkRatio > areaRatio) {
      calcArtworkWidth = areaRect.w - CORRECTION_FACTOR;
      calcArtworkHeight = (calcArtworkWidth / pngWidthPixels) * pngHeightPixels;
    }
    // Artwork is more portrait than area - fit to height
    else {
      calcArtworkHeight = areaRect.h - CORRECTION_FACTOR;
      calcArtworkWidth = (calcArtworkHeight / pngHeightPixels) * pngWidthPixels;
    }

    const gapW = areaRect.w - calcArtworkWidth;
    const gapH = areaRect.h - calcArtworkHeight;

    shapeX = areaRect.x + gapW / 2;
    shapeY = areaRect.y + gapH / 2;
  }

  if (printableArea?.kind === 'Ellipse') {
    const min =
      printableArea.rx < printableArea.ry ? printableArea.rx : printableArea.ry;
    let size = 0;
    if (Math.abs(printableArea.ry - printableArea.rx) > 630) {
      size = min * 2;
    } else {
      size = min * 1.6;
    }

    if (aspectRatioWH >= aspectRatioHW) {
      calcArtworkWidth = (size * (artworkSpaceConsumed - 15)) / 100;
      calcArtworkHeight = calcArtworkWidth / aspectRatioWH;
    } else {
      calcArtworkHeight = (size * (artworkSpaceConsumed - 15)) / 100;
      calcArtworkWidth = calcArtworkHeight / aspectRatioHW;
    }

    shapeX = printableArea.x - calcArtworkWidth / 2;
    shapeY = printableArea.y - calcArtworkHeight / 2;
  }

  if (printableArea?.kind === 'Polygon') {
    const size = polygonDetails.minDistance;

    if (aspectRatioWH >= aspectRatioHW) {
      calcArtworkWidth = (size * artworkSpaceConsumed) / 100;
      calcArtworkHeight = calcArtworkWidth / aspectRatioWH;
    } else {
      calcArtworkHeight = (size * artworkSpaceConsumed) / 100;
      calcArtworkWidth = calcArtworkHeight / aspectRatioHW;
    }

    shapeX = polygonDetails.x - calcArtworkWidth / 2;
    shapeY = polygonDetails.y - calcArtworkHeight / 2;
  }

  return {
    x: shapeX,
    y: shapeY,
    width: calcArtworkWidth,
    height: calcArtworkHeight,
  };
}

export function getShapePositionInPX(
  printArea: ProductPrintArea,
  shapeDistanceFromLeft: number,
  shapeDistanceFromTop: number,
  shapeWidth: number,
  shapeHeight: number,
  scaledArea: PrintArea | undefined
) {
  const productPrintAreaPoints = printArea.productPrintAreaPoints;

  const printPosition = productPrintAreaPoints.find(
    (point) => point.sequenceNo == 1
  );

  if (printPosition && scaledArea) {
    const widthAreaInMM = printArea.widthMm;
    const heightAreaInMM = printArea.heightMm;

    const xInMM = shapeDistanceFromLeft - printPosition.distanceFromLeftEdge;
    const yInMM = shapeDistanceFromTop - printPosition.distanceFromTop;

    const xInPercentage = (xInMM * 100) / widthAreaInMM;
    const yInPercentage = (yInMM * 100) / heightAreaInMM;

    if (scaledArea?.kind === 'Rectangle') {
      const xInPX = (xInPercentage * scaledArea.width) / 100 + scaledArea.x;
      const yInPX = (yInPercentage * scaledArea.height) / 100 + scaledArea.y;

      const widthInPX =
        (((100 * shapeWidth) / widthAreaInMM) * scaledArea.width) / 100;
      const heightInPX =
        (((100 * shapeHeight) / heightAreaInMM) * scaledArea.height) / 100;

      return { xInPX, yInPX, widthInPX, heightInPX };
    }

    if (scaledArea?.kind === 'Ellipse') {
      const xInPX =
        (xInPercentage * (scaledArea.rx * 2)) / 100 +
        (scaledArea.x - scaledArea.rx);
      const yInPX =
        (yInPercentage * (scaledArea.ry * 2)) / 100 +
        (scaledArea.y - scaledArea.ry);

      const widthInPX =
        (((100 * shapeWidth) / widthAreaInMM) * (scaledArea.rx * 2)) / 100;
      const heightInPX =
        (((100 * shapeHeight) / heightAreaInMM) * (scaledArea.ry * 2)) / 100;

      return { xInPX, yInPX, widthInPX, heightInPX };
    }
  }

  if (scaledArea?.kind === 'Polygon') {
    const minXMM = Math.min(
      ...productPrintAreaPoints.map(
        (printArea) => printArea.distanceFromLeftEdge
      )
    );
    const maxXMM = Math.max(
      ...productPrintAreaPoints.map(
        (printArea) => printArea.distanceFromLeftEdge
      )
    );
    const minYMM = Math.min(
      ...productPrintAreaPoints.map((printArea) => printArea.distanceFromTop)
    );
    const maxYMM = Math.max(
      ...productPrintAreaPoints.map((printArea) => printArea.distanceFromTop)
    );

    const areaWidthInMM = maxXMM - minXMM;
    const areaHeightInMM = maxYMM - minYMM;

    const xInAreaMM = shapeDistanceFromLeft - minXMM;
    const yInAreaMM = shapeDistanceFromTop - minYMM;

    const xInPercentage = (xInAreaMM * 100) / areaWidthInMM;
    const yInPercentage = (yInAreaMM * 100) / areaHeightInMM;

    const minXPX = Math.min(...scaledArea.points.map((x) => x.x));
    const maxXPX = Math.max(...scaledArea.points.map((x) => x.x));
    const minYPX = Math.min(...scaledArea.points.map((x) => x.y));
    const maxYPX = Math.max(...scaledArea.points.map((x) => x.y));

    const scaledAreaWInPX = maxXPX - minXPX;
    const scaledAreaHInPX = maxYPX - minYPX;

    const xInPX = (xInPercentage * scaledAreaWInPX) / 100 + minXPX;
    const yInPX = (yInPercentage * scaledAreaHInPX) / 100 + minYPX;

    const widthInPX =
      (((100 * shapeWidth) / areaWidthInMM) * scaledAreaWInPX) / 100;
    const heightInPX =
      (((100 * shapeHeight) / areaHeightInMM) * scaledAreaHInPX) / 100;

    return { xInPX, yInPX, widthInPX, heightInPX };
  }

  return { xInPX: 0, yInPX: 0, widthInPX: 0, heightInPX: 0 };
}
